import { TreeSelect, Typography } from 'antd'
import { useFFEStore } from '../../store/ffeStore'
import { useMemo } from 'react'

const { Text } = Typography
type Children = Array<{
  title: string
  value: string
  disabled?: boolean
  children: Children
}>

export const GroupSelectorPopoverContent = ({
  entityId,
  parentGroupId,
  entity,
  onClose
}: {
  entityId: string
  parentGroupId?: string
  entity?: 'group' | 'lineItem'
  onClose: () => void
}) => {
  const groups = useFFEStore((state) => state.document.groups)
  const updateGroup = useFFEStore((state) => state.updateGroup)
  const updateLineItem = useFFEStore((state) => state.updateLineItem)
  const moveLineItemToBottomOfGroup = useFFEStore(
    (state) => state.moveLineItemToBottomOfGroup
  )

  const getAllChildGroupIds = (groupId: string): Array<string> => {
    const children = groups.filter((g) => g.groupId === groupId)
    if (children.length === 0) return []
    return children.flatMap((g) => [g.id, ...getAllChildGroupIds(g.id)])
  }

  const handleGroupChange = (newGroupId: string | null) => {
    if (entity === 'group') {
      updateGroup(entityId, { groupId: newGroupId || undefined })
    } else {
      updateLineItem(entityId, { groupId: newGroupId || undefined })
      moveLineItemToBottomOfGroup(entityId)
    }
    onClose()
  }

  const allowedGroups = useMemo(() => {
    if (entity === 'lineItem') return groups
    // exlude self and all child groups
    const notAllowed = [entityId, ...getAllChildGroupIds(entityId)]
    return groups.filter((g) => !notAllowed.includes(g.id))
  }, [groups, entity, entityId])

  const getGroupTree = (groupId?: string): Children => {
    const group = allowedGroups.find((group) => group.id === groupId)
    if (!group) return []

    const children = allowedGroups.filter((group) => group.groupId === groupId)
    return children.map((child) => ({
      title: child.name,
      value: child.id,
      disabled: child.id === parentGroupId || child.id === groupId,
      children: getGroupTree(child.id)
    }))
  }

  const treeGroups = useMemo(() => {
    return allowedGroups
      .filter((group) => !group.groupId)
      .map((group) => ({
        title: group.name,
        value: group.id,
        disabled: group.id === parentGroupId,
        children: getGroupTree(group.id)
      }))
  }, [groups, entityId])

  return (
    <div className="w-[250px]">
      <Text type="secondary" className="block mb-2">
        {entity === 'group' ? 'Make subgroup of:' : 'Move to group:'}
      </Text>

      <TreeSelect
        open={true}
        showSearch={false}
        value={parentGroupId}
        placeholder="Select a group"
        className="w-full"
        allowClear
        treeDefaultExpandAll
        onChange={handleGroupChange}
        treeData={treeGroups}
      />
    </div>
  )
}
